import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { ScrollView, ScrollViewProps, View, Platform, StyleSheet } from 'react-native';

interface WebScrollViewProps extends ScrollViewProps {
  children: React.ReactNode;
  contentContainerStyle?: any;
}

/**
 * WebScrollView - A cross-platform ScrollView that uses native browser scrolling on web
 * 
 * This component solves the issue where React Native Web's ScrollView causes
 * page refreshes on mobile browsers when the browser chrome (URL bar) shows/hides.
 * 
 * On web: Uses native <div> with overflow scrolling (no React re-renders on scroll)
 * On native: Uses standard React Native ScrollView
 */
const WebScrollView = forwardRef<ScrollView, WebScrollViewProps>(({ 
  children, 
  style,
  contentContainerStyle,
  showsVerticalScrollIndicator = true,
  showsHorizontalScrollIndicator = true,
  horizontal = false,
  onScroll,
  scrollEventThrottle,
  ...props 
}, ref) => {
  const divRef = useRef<HTMLDivElement>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const stableHeightTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeightRef = useRef<number>(0);
  const [explicitHeight, setExplicitHeight] = useState<number | undefined>(undefined);

  // Measure parent container and set explicit height for web scrolling
  useEffect(() => {
    if (Platform.OS === 'web') {
      let debounceTimer: NodeJS.Timeout | null = null;
      
      const updateHeight = () => {
        const element = divRef.current;
        if (element && element.parentElement) {
          const parent = element.parentElement;
          const parentRect = parent.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(parent);
          const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
          const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
          const availableHeight = parentRect.height - paddingTop - paddingBottom;
          
          // Only set if we have flex: 1 and parent has constrained height
          const flatStyle = StyleSheet.flatten(style);
          if (flatStyle?.flex === 1 && availableHeight > 0) {
            // Only update if height actually changed significantly (avoid unnecessary re-renders)
            setExplicitHeight(prev => {
              // Only update if change is significant (> 100px) or if not set yet
              // Increased threshold to reduce frequent updates that cause scroll issues
              if (prev === undefined || Math.abs(prev - availableHeight) > 100) {
                // Don't mess with scroll position at all - let browser handle it naturally
                return availableHeight;
              }
              return prev;
            });
          }
        }
      };

      // Debounced update function - only update if height is stable
      const debouncedUpdateHeight = () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
          const element = divRef.current;
          if (element?.parentElement) {
            const parentRect = element.parentElement.getBoundingClientRect();
            const currentHeight = parentRect.height;
            
            // Only update if height has been stable for 500ms
            if (stableHeightTimerRef.current) {
              clearTimeout(stableHeightTimerRef.current);
            }
            
            if (Math.abs(currentHeight - lastHeightRef.current) > 20) {
              // Height changed significantly, reset timer
              lastHeightRef.current = currentHeight;
              stableHeightTimerRef.current = setTimeout(() => {
                // Height has been stable for 1 second, update
                requestAnimationFrame(updateHeight);
              }, 1000);
            } else {
              // Height is stable, update immediately if not already set
              setExplicitHeight(prev => {
                if (prev === undefined) {
                  requestAnimationFrame(updateHeight);
                }
                return prev;
              });
            }
          }
        }, 200); // 200ms debounce - increased to reduce frequency
      };

      // Wait for DOM to be ready, then measure
      const timeoutId = setTimeout(() => {
        updateHeight();
        
        // Use ResizeObserver to watch for parent size changes (debounced)
        if (divRef.current?.parentElement && !resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver(debouncedUpdateHeight);
          resizeObserverRef.current.observe(divRef.current.parentElement);
        }
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        if (stableHeightTimerRef.current) {
          clearTimeout(stableHeightTimerRef.current);
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      };
    }
  }, [style]);

  // Expose scrollToEnd / scrollTo / scrollBy methods for compatibility
  useImperativeHandle(ref, () => ({
    scrollToEnd: (options?: { animated?: boolean }) => {
      if (Platform.OS === 'web' && divRef.current) {
        // Scroll to the maximum scroll position
        const scrollHeight = divRef.current.scrollHeight;
        const clientHeight = divRef.current.clientHeight;
        const maxScroll = Math.max(0, scrollHeight - clientHeight);
        
        // Use scrollTop directly for instant scroll (faster and more reliable)
        if (options?.animated) {
          divRef.current.scrollTo({
            top: maxScroll,
            behavior: 'smooth',
          });
        } else {
          // Instant scroll - set scrollTop directly (most reliable)
          divRef.current.scrollTop = maxScroll;
        }
      } else if (scrollViewRef.current) {
        scrollViewRef.current.scrollToEnd(options);
      }
    },
    scrollTo: (options: any) => {
      if (Platform.OS === 'web' && divRef.current) {
        divRef.current.scrollTo({
          top: options.y || 0,
          left: options.x || 0,
          behavior: options.animated ? 'smooth' : 'auto',
        });
      } else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo(options);
      }
    },
    /**
     * Scroll by a delta. Used e.g. for auto-scrolling while drag-reordering
     * a list. Web-only; on native we don't currently need this and the RN
     * ScrollView doesn't expose a delta-based scroll without tracking offset.
     */
    scrollBy: (options: { y?: number; x?: number; animated?: boolean }) => {
      if (Platform.OS === 'web' && divRef.current) {
        const dy = options.y || 0;
        const dx = options.x || 0;
        if (options.animated) {
          divRef.current.scrollBy({ top: dy, left: dx, behavior: 'smooth' });
        } else {
          // Direct scrollTop assignment is the most reliable way to update
          // in tight loops (e.g. requestAnimationFrame for auto-scroll).
          divRef.current.scrollTop += dy;
          divRef.current.scrollLeft += dx;
        }
      }
    },
    /**
     * Get the underlying scrollable DOM element on web. Returns null on native.
     * Useful for measuring the scroll viewport (e.g. to detect when a drag is
     * near the top/bottom edge for auto-scroll).
     */
    getScrollElement: (): HTMLElement | null => {
      if (Platform.OS === 'web') {
        return divRef.current;
      }
      return null;
    },
  } as any));

  if (Platform.OS === 'web') {
    // Use native browser scrolling on web to avoid React re-renders
    // when mobile browser chrome shows/hides
    const flatStyle = StyleSheet.flatten(style);
    const flatContentStyle = StyleSheet.flatten(contentContainerStyle);
    
    // Handle scrollbar visibility for both vertical and horizontal
    const verticalScrollbarStyle = showsVerticalScrollIndicator ? {} : {
      scrollbarWidth: 'none' as any, // Firefox
      msOverflowStyle: 'none' as any, // IE/Edge
    };
    const horizontalScrollbarStyle = showsHorizontalScrollIndicator ? {} : {
      scrollbarWidth: 'none' as any, // Firefox
      msOverflowStyle: 'none' as any, // IE/Edge
    };
    const scrollbarStyle = horizontal ? horizontalScrollbarStyle : verticalScrollbarStyle;

    // Extract flex: 1 and convert to proper CSS constraints
    const hasFlex = flatStyle?.flex === 1;
    const webStyle: React.CSSProperties = {
      ...flatStyle,
      WebkitOverflowScrolling: 'touch',
      ...scrollbarStyle,
    };

    // Set overflow based on horizontal prop
    if (horizontal) {
      webStyle.overflowX = 'auto';
      webStyle.overflowY = 'hidden';
      webStyle.display = 'flex';
      webStyle.flexDirection = 'row';
      // Ensure content doesn't wrap
      webStyle.flexWrap = 'nowrap';
    } else {
      webStyle.overflowY = 'auto';
      webStyle.overflowX = 'hidden';
    }

    // If we have an explicit height from ResizeObserver, use it (only for vertical scrolling)
    if (!horizontal && explicitHeight !== undefined) {
      webStyle.height = `${explicitHeight}px`;
      webStyle.maxHeight = `${explicitHeight}px`;
      webStyle.minHeight = 0;
    } else if (!horizontal && hasFlex) {
      // Fallback: try flexbox constraints (only for vertical scrolling)
      webStyle.display = 'flex';
      webStyle.flexDirection = 'column';
      webStyle.minHeight = 0;
      webStyle.height = '100%';
      webStyle.maxHeight = '100%';
    }

    // Content container style - set flexDirection for horizontal scrolling
    const contentStyle: React.CSSProperties = {
      ...flatContentStyle,
    };
    if (horizontal) {
      contentStyle.display = 'flex';
      contentStyle.flexDirection = 'row';
      contentStyle.flexWrap = 'nowrap';
    } else {
      // Match RN ScrollView: content container is a column flexbox so flexGrow + justifyContent work.
      // minHeight lets short content stay vertically centered when the scroll area has a fixed height
      // (e.g. ResizeObserver path omits display:flex on the outer div).
      contentStyle.display = 'flex';
      contentStyle.flexDirection = 'column';
      if (flatContentStyle?.justifyContent != null) {
        contentStyle.minHeight = '100%';
      }
    }

    // Build a synthetic-like scroll event on web that mirrors the shape of
    // React Native's NativeSyntheticEvent<NativeScrollEvent> consumers expect.
    const handleWebScroll = onScroll
      ? (e: React.UIEvent<HTMLDivElement>) => {
          const target = e.currentTarget;
          const syntheticEvent: any = {
            nativeEvent: {
              contentOffset: { x: target.scrollLeft, y: target.scrollTop },
              contentSize: { width: target.scrollWidth, height: target.scrollHeight },
              layoutMeasurement: { width: target.clientWidth, height: target.clientHeight },
            },
          };
          (onScroll as any)(syntheticEvent);
        }
      : undefined;

    return (
      <div 
        ref={divRef}
        style={webStyle}
        className={(horizontal ? showsHorizontalScrollIndicator : showsVerticalScrollIndicator) ? '' : 'hide-scrollbar'}
        onScroll={handleWebScroll}
      >
        {/* Use plain div instead of React Native View to avoid layout event listeners */}
        <div style={contentStyle}>
          {children}
        </div>
      </div>
    );
  }

  // Use React Native ScrollView on native platforms
  return (
    <ScrollView 
      ref={scrollViewRef}
      style={style}
      contentContainerStyle={contentContainerStyle}
      showsVerticalScrollIndicator={showsVerticalScrollIndicator}
      showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
      horizontal={horizontal}
      onScroll={onScroll}
      scrollEventThrottle={scrollEventThrottle}
      {...props}
    >
      {children}
    </ScrollView>
  );
});

WebScrollView.displayName = 'WebScrollView';

export default WebScrollView;

